const Format = {};

/**
 * Format an account number for display
 * @param {number} account Account number
 * @return {string} Display formatted account number
 */
Format.bankAccountNumber = account => {
  if ((typeof account) !== 'object' || (typeof account.toString) !== 'function') {
    return account;
  }

  const str = account.toString();
  if (str.length === 11) {
    return `${str.substring(0, 4)}.${str.substring(4, 2)}.${str.substring(6)}`;
  }

  return account;
};

Format.joinNotBlank = (items = [], separator) => {
  return items.filter(v => !!v && v.trim() !== '').join(separator);
};

Format.formatAddressLine = (address) => {

  const { cadastreId, addressLine1, addressLine2, addressLine3, postalCode, postalPlace  } = address || {};

  const parts = [
    cadastreId,
    addressLine1,
    addressLine2,
    addressLine3,
    Format.joinNotBlank([postalCode, postalPlace], ' ')
  ];

  return Format.joinNotBlank(parts, ', ');
};

Format.organizationNumber = (orgNumber, countryCode) => {
  const stringNumber = String(orgNumber);

  const formats = {
    NO: ['### ### ###', 9],
    IS: ['######-####', 10],
    SE: ['######-####', 10]
  };
  const format = formats[countryCode]?.[1] === stringNumber.length ? formats[countryCode][0] : null;
  const tempStringNumber = stringNumber.split('');
  return format ? format.replaceAll('#', () => tempStringNumber.shift()) : stringNumber;
};

Format.normalizeOrgNumber = orgNumber => String(orgNumber).replace(/[^A-Za-z0-9]/g, '');

Format.organizationNumberWithLink = (orgNumber, countryCode) => {
  let link = null;
  if (countryCode === 'NO') {
    link = `https://w2.brreg.no/enhet/sok/detalj.jsp?orgnr=${Format.normalizeOrgNumber(orgNumber)}`;
  } else if (countryCode === 'IS') {
    link = `https://www.rsk.is/fyrirtaekjaskra/leit/kennitala/${Format.normalizeOrgNumber(orgNumber)}`;
  }

  const formattedNumber = Format.organizationNumber(orgNumber, countryCode);

  return link ? <a href={link} target="_blank" rel="noreferrer">{formattedNumber}</a> : formattedNumber;
};


Format.financialEInvoicingLink = (countryCode) => {
  let link = null;
  if (countryCode === 'SE') {
    link = `https://docs.moment.team/help/financial/e-invoicing/countries/sweden`;
  } else if (countryCode === 'DK') {
    link = `https://docs.moment.team/help/financial/e-invoicing/countries/denmark`;
  }
  return link
};



Format.formatOverflowString = (string = '', length, overflow = '...') => {
  return string && length && string.length > length ? string.substr(0, length - overflow.length) + overflow : string;
};

Format.isBlank = (value) => {
  return value === undefined || value === null || value.toString().trim().length === 0;
};

Format.formatString = (value, defaultValue = '') => {
  return Format.isBlank(value) ? defaultValue : value;
};

Format.formatStringToArray = (value, defaultValue = '', separator = ',') => {
  return Format.formatString(value, defaultValue).split(separator).filter((s) => !Format.isBlank(s));
};

Format.formatArrayToString = (value, { separator = ',', includeAlways = [] } = {}) => {

  const result = (value ?? []).join(separator);

  if (includeAlways.length > 0) {
    const existing = new Set(result);
    for (const a of includeAlways) {
      if (!existing.has(a)) {
        result.push(a);
      }
    }
  }

  return result;
};

Format.formatStringToNumber = (value, defaultValue = null) => {

  const isBlank = Format.isBlank(value);
  if (typeof defaultValue === 'number' && isBlank) {
    return defaultValue;
  }

  if (!isBlank && typeof value === 'string') {
    value = value.trim();
  }

  return isBlank ? null : 1 * value;
};

Format.formatStringToNumberArray = (value, defaultValue = '', separator = ',') => {
  const strings = Format.formatStringToArray(value, defaultValue, separator);
  const numbers = [];
  for (const str of strings) {
    const n = Format.formatStringToNumber(str);
    if (n || n === 0) {
      numbers.push(n);
    }
  }
  return numbers;
};

Format.formatStringToBoolean = (value, defaultValue) => {
  const _defaultValue = typeof defaultValue === 'boolean' ? defaultValue : null;
  if (typeof _defaultValue === 'boolean' && Format.isBlank(value)) {
    return _defaultValue;
  }
  return value === 'false'
    ? false : value === 'true'
      ? true : !!value;
};

Format.reverseFormatNumber = (val, locale, silent = false) => {
  val = val.replace('−', '-'); // Intl.NumberFormat pushes 8722 character, while it is invalid as a part of the number, replacing with 45
  const parts = new Intl.NumberFormat(locale).formatToParts(1111.1);
  const group = parts.find(part => part.type === 'group').value;
  const decimal = parts.find(part => part.type === 'decimal').value;
  const reversedVal = val
    .replace(new RegExp('\\' + group, 'g'), '')
    .replace(new RegExp('\\' + decimal, 'g'), '.')
    .replace(/[^\d.-]/g, '');
  if (Number.isNaN(+reversedVal) && !silent) {
    console.error(`Failed parsing number: "${val}"`);
  }
  return +reversedVal;
};

Format.formatNumber = (number) => new Intl.NumberFormat('nb').format(number);
Format.formatDecimal = (number, decimal = 2) => new Intl.NumberFormat('nb', { minimumFractionDigits: decimal, maximumFractionDigits: decimal, style: 'decimal' }).format(number);

Format.formatMoney = (value, currency, decimals = 0, notation, allowZero = false) => {
  const _currency = currency || undefined;
  if (!value && !allowZero) {
    return '';
  }
  return new Intl.NumberFormat('nb-NO', {
    style: _currency ? 'currency' : 'decimal',
    currency, currencyDisplay: 'code',
    notation: notation,
    maximumFractionDigits: decimals
  }).format(value);
};

Format.formatUserNameToInitials = (fullName, firstName, lastName) => {
  const name = fullName || (firstName && (lastName ? `${firstName} ${lastName}` : firstName)) || '';

  if (!name) {
    return '';
  }

  const nameParts = name.split(' ');
  const firstNameInitial = nameParts[0].charAt(0);

  if (nameParts.length > 1) {
    const lastNameInitial = nameParts.pop().charAt(0);
    return `${firstNameInitial}${lastNameInitial}`;
  }

  return firstNameInitial;
};

export const inputNumberFormatterWrapper = (formatter) => (value, { userTyping, input }) => {
  return userTyping && (!['0,00', '0,0', '0,', '0.00', '0.0', '0.'].includes(input)) ? input : formatter(value);
}

export default Format;
